import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, RefreshCw, MessageCircle, Bot, PauseCircle, PlayCircle, FlaskConical, Trash2, Plus, GraduationCap, Pencil, Download } from "lucide-react";
import { fetchInboxConversations, fetchInboxMessages, markInboxRead, sendInboxReply,
         fetchAiSettings, updateAiSettings, pauseAi, resumeAi, testAiDraft,
         fetchAiKnowledge, addAiKnowledge, editAiKnowledge, deleteAiKnowledge,
         approveAiKnowledge, rejectAiKnowledge, approveAllPendingKnowledge, crawlKnowledge,
         exportInboxCsv, formatApiError } from "@/lib/api";

const inp = "w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-saffron";

const KB_SOURCE = { crawled: ["Crawled", "bg-sky-100 text-sky-700"], "ai-generated": ["AI-drafted", "bg-purple-100 text-purple-700"], manual: ["Manual", "bg-stone-100 text-stone-500"] };
const KB_TABS = [["pending", "Pending review"], ["approved", "Live"], ["rejected", "Rejected"], ["all", "All"]];

function KnowledgeTrainer({ token }) {
    const [items, setItems] = useState(null);
    const [counts, setCounts] = useState({});
    const [tab, setTab] = useState("pending");
    const [q, setQ] = useState("");
    const [a, setA] = useState("");
    const [busy, setBusy] = useState(false);
    const [crawling, setCrawling] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editQ, setEditQ] = useState("");
    const [editA, setEditA] = useState("");

    const load = useCallback(async () => {
        try {
            const d = await fetchAiKnowledge(token);
            setItems(d.items);
            setCounts(d.counts || {});
        } catch { toast.error("Could not load knowledge base"); }
    }, [token]);
    useEffect(() => { load(); }, [load]);

    const add = async () => {
        if (!q.trim() || !a.trim()) { toast.error("Both question and answer are needed"); return; }
        setBusy(true);
        try {
            await addAiKnowledge(token, { question: q.trim(), answer: a.trim() });
            setQ(""); setA("");
            toast.success("Added — the AI will use this answer immediately");
            load();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const toggle = async (item) => {
        try {
            await editAiKnowledge(token, item.id, { enabled: !item.enabled });
            toast.success(item.enabled ? "Disabled — the bot will skip this question" : "Enabled — live for customers");
            load();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };

    const startEdit = (item) => {
        setEditingId(item.id);
        setEditQ(item.question);
        setEditA(item.answer);
    };

    const saveEdit = async (item) => {
        if (!editQ.trim() || !editA.trim()) { toast.error("Question and answer both required"); return; }
        try {
            await editAiKnowledge(token, item.id, { question: editQ.trim(), answer: editA.trim() });
            setEditingId(null);
            toast.success("Saved — your edited reply is now live");
            load();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };

    const remove = async (item) => {
        if (!window.confirm(`Delete “${item.question.slice(0, 50)}”?`)) return;
        try { await deleteAiKnowledge(token, item.id); toast.success("Removed from training"); load(); }
        catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };

    const review = async (item, action) => {
        try {
            if (action === "approve") { await approveAiKnowledge(token, item.id); toast.success("Approved — live for customers"); }
            else { await rejectAiKnowledge(token, item.id); toast.success("Rejected — the bot will not use it"); }
            load();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };

    const approveAll = async () => {
        if (!window.confirm(`Approve all ${counts.pending || 0} pending entries? They go live for customers immediately.`)) return;
        try {
            const r = await approveAllPendingKnowledge(token);
            toast.success(`${r.approved} entries approved and live`);
            load();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };

    const recrawl = async () => {
        setCrawling(true);
        try {
            const s = await crawlKnowledge(token);
            toast.success(`Crawl done: ${s.created} new, ${s.updated} changed (back for review), ${s.unchanged} unchanged, ${s.stale} stale`);
            load();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
        finally { setCrawling(false); }
    };

    const shown = (items || []).filter((it) => {
        const st = it.status || "approved";
        if (tab === "all") return true;
        if (tab === "approved") return st === "approved";
        return st === tab;
    });

    return (
        <div className="md:col-span-2 rounded-xl border border-stone-200 p-4" data-testid="ai-knowledge-trainer">
            <div className="flex items-center gap-2 flex-wrap">
                <GraduationCap className="h-4 w-4 text-navy" />
                <p className="text-[11px] font-black uppercase tracking-wider text-navy">Train the bot — FAQs & knowledge</p>
                <span className="ml-auto text-[10px] text-stone-400" data-testid="kb-count">{items ? `${items.length} entries` : ""}</span>
                <button onClick={recrawl} disabled={crawling} data-testid="kb-recrawl"
                    className="inline-flex items-center gap-1.5 rounded-full bg-navy px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-white hover:bg-navy-light disabled:opacity-50">
                    {crawling ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Re-crawl packages
                </button>
            </div>
            <p className="mt-1 text-[11px] text-stone-400">
                The crawler reads your live packages daily (7am IST) and drafts Q&A — nothing reaches customers until you approve it here.
                Manual entries go live instantly.
            </p>

            <div className="mt-3 grid md:grid-cols-2 gap-2">
                <input className={inp} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Customer question, e.g. What is your cancellation policy?" data-testid="kb-question" />
                <div className="flex gap-2">
                    <input className={inp} value={a} onChange={(e) => setA(e.target.value)} placeholder="The answer the bot should give"
                        data-testid="kb-answer" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
                    <button onClick={add} disabled={busy} data-testid="kb-add"
                        className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-[11px] font-black uppercase tracking-wider text-white hover:bg-navy-light disabled:opacity-50">
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add
                    </button>
                </div>
            </div>

            <div className="mt-3 flex items-center gap-1.5 flex-wrap" data-testid="kb-tabs">
                {KB_TABS.map(([id, label]) => (
                    <button key={id} onClick={() => setTab(id)} data-testid={`kb-tab-${id}`}
                        className={`rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-wider ${tab === id ? "bg-navy text-white" : "bg-stone-100 text-stone-500 hover:bg-stone-200"}`}>
                        {label}{id !== "all" && counts[id] ? ` (${counts[id]})` : ""}
                    </button>
                ))}
                {tab === "pending" && (counts.pending || 0) > 0 && (
                    <button onClick={approveAll} data-testid="kb-approve-all"
                        className="ml-auto rounded-full bg-green-600 px-3 py-1 text-[10px] font-black uppercase tracking-wider text-white hover:brightness-95">
                        Approve all ({counts.pending})
                    </button>
                )}
            </div>

            <div className="mt-3 space-y-1.5 max-h-72 overflow-y-auto" data-testid="kb-list">
                {items === null ? <div className="py-4 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-saffron" /></div>
                    : shown.length === 0 ? <p className="py-3 text-xs text-stone-400 text-center">
                        {tab === "pending" ? "Nothing pending — run “Re-crawl packages” or add FAQs above." : "Nothing here yet."}</p>
                        : shown.map((it) => {
                            const st = it.status || "approved";
                            const src = KB_SOURCE[it.source] || KB_SOURCE.manual;
                            return (
                                <div key={it.id} className={`rounded-lg border px-3 py-2 ${st === "pending" ? "border-amber-200 bg-amber-50/50" : it.enabled ? "border-stone-200 bg-white" : "border-stone-100 bg-stone-50 opacity-60"}`} data-testid={`kb-item-${it.id}`}>
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className={`rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-wider ${src[1]}`}>{src[0]}</span>
                                        {it.package_slug && <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[9px] font-bold text-stone-500">{it.package_slug}</span>}
                                        {st === "pending" && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-black uppercase text-amber-700">Needs review</span>}
                                        {st === "stale" && <span className="rounded-full bg-red-100 px-2 py-0.5 text-[9px] font-black uppercase text-red-600">Package offline</span>}
                                    </div>
                                    <p className="mt-1 text-sm font-bold text-navy">{it.question}</p>
                                    <p className="text-xs text-stone-500">{it.answer}</p>
                                    {editingId === it.id && (
                                        <div className="mt-2 space-y-1.5 rounded-lg border border-saffron/40 bg-white p-2.5" data-testid={`kb-edit-form-${it.id}`}>
                                            <input className={inp} value={editQ} onChange={(e) => setEditQ(e.target.value)} placeholder="Question" data-testid={`kb-edit-q-${it.id}`} />
                                            <textarea className={inp} rows={3} value={editA} onChange={(e) => setEditA(e.target.value)} placeholder="The reply the bot should give" data-testid={`kb-edit-a-${it.id}`} />
                                            <div className="flex gap-2">
                                                <button onClick={() => saveEdit(it)} data-testid={`kb-edit-save-${it.id}`}
                                                    className="rounded-full bg-saffron px-3 py-1 text-[9px] font-black uppercase tracking-wider text-white hover:brightness-95">Save reply</button>
                                                <button onClick={() => setEditingId(null)} data-testid={`kb-edit-cancel-${it.id}`}
                                                    className="rounded-full bg-stone-100 px-3 py-1 text-[9px] font-black uppercase tracking-wider text-stone-500 hover:bg-stone-200">Cancel</button>
                                            </div>
                                        </div>
                                    )}
                                    <div className="mt-1.5 flex items-center gap-2">
                                        {st !== "approved" && st !== "stale" && editingId !== it.id && (
                                            <button onClick={() => review(it, "approve")} data-testid={`kb-approve-${it.id}`}
                                                className="rounded-full bg-green-600 px-2.5 py-1 text-[9px] font-black uppercase tracking-wider text-white hover:brightness-95">Approve</button>
                                        )}
                                        {st === "pending" && editingId !== it.id && (
                                            <button onClick={() => review(it, "reject")} data-testid={`kb-reject-${it.id}`}
                                                className="rounded-full bg-red-100 px-2.5 py-1 text-[9px] font-black uppercase tracking-wider text-red-600 hover:bg-red-200">Reject</button>
                                        )}
                                        {st === "approved" && editingId !== it.id && (
                                            <button onClick={() => toggle(it)} data-testid={`kb-toggle-${it.id}`}
                                                className={`rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-wider ${it.enabled ? "bg-green-100 text-green-700" : "bg-stone-200 text-stone-500"}`}>
                                                {it.enabled ? "On" : "Off"}
                                            </button>
                                        )}
                                        {st !== "stale" && editingId !== it.id && (
                                            <button onClick={() => startEdit(it)} data-testid={`kb-edit-${it.id}`} title="Edit reply"
                                                className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2.5 py-1 text-[9px] font-black uppercase tracking-wider text-stone-600 hover:bg-stone-200">
                                                <Pencil className="h-3 w-3" /> Edit
                                            </button>
                                        )}
                                        <button onClick={() => remove(it)} title="Delete" data-testid={`kb-delete-${it.id}`} className="ml-auto text-stone-300 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                                    </div>
                                </div>
                            );
                        })}
            </div>
        </div>
    );
}

function AiAgentPanel({ token, pausedPhones, onChanged }) {
    const [open, setOpen] = useState(false);
    const [notes, setNotes] = useState(null);
    const [testMsg, setTestMsg] = useState("");
    const [testOut, setTestOut] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => { fetchAiSettings(token).then((d) => setNotes(d.settings.business_notes)).catch(() => {}); }, [token]);

    const saveNotes = async () => {
        setBusy(true);
        try { await updateAiSettings(token, { business_notes: notes }); toast.success("AI instructions saved"); onChanged?.(); }
        catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const runTest = async () => {
        if (!testMsg.trim()) return;
        setBusy(true); setTestOut(null);
        try { setTestOut(await testAiDraft(token, testMsg.trim())); }
        catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <div className="mt-3 rounded-xl border border-stone-200 overflow-hidden" data-testid="ai-agent-panel">
            <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-cream/50" data-testid="ai-panel-toggle">
                <Bot className="h-4 w-4 text-navy" />
                <span className="text-xs font-black uppercase tracking-wider text-navy">AI agent settings & playground</span>
                <span className="ml-auto text-[10px] text-stone-400">{open ? "Hide" : "Show"}</span>
            </button>
            {open && (
                <div className="border-t border-stone-200 p-4 grid md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-[11px] font-bold uppercase tracking-wider text-stone-500 mb-1">Extra instructions for the AI (offers, notes)</label>
                        <textarea rows={5} className={inp} value={notes ?? ""} onChange={(e) => setNotes(e.target.value)} data-testid="ai-notes-input" />
                        <button onClick={saveNotes} disabled={busy} data-testid="ai-notes-save"
                            className="mt-2 px-4 py-2 rounded-full bg-navy text-white text-[11px] font-black uppercase tracking-wider hover:bg-navy-light disabled:opacity-50">Save instructions</button>
                        <p className="mt-2 text-[11px] text-stone-400">The AI also reads your live package catalogue automatically — it can never invent prices.</p>
                    </div>
                    <div>
                        <label className="block text-[11px] font-bold uppercase tracking-wider text-stone-500 mb-1">Test playground (drafts only — never sends)</label>
                        <div className="flex gap-2">
                            <input className={inp} value={testMsg} onChange={(e) => setTestMsg(e.target.value)} placeholder="e.g. Goa trip for 30 college students?" data-testid="ai-test-input"
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runTest(); } }} />
                            <button onClick={runTest} disabled={busy || !testMsg.trim()} data-testid="ai-test-run"
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-navy text-navy text-[11px] font-black uppercase tracking-wider hover:bg-navy hover:text-white disabled:opacity-50">
                                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FlaskConical className="h-3.5 w-3.5" />} Draft
                            </button>
                        </div>
                        {testOut && (
                            <div className="mt-3 rounded-lg bg-[#E7F6E9] border border-[#25D366]/30 p-3" data-testid="ai-test-output">
                                <p className="text-sm text-stone-800 whitespace-pre-wrap">{testOut.draft}</p>
                                {testOut.would_handoff && <p className="mt-2 text-[10px] font-black uppercase tracking-wider text-amber-700">AI would request human handoff for this message</p>}
                            </div>
                        )}
                    </div>
                    <KnowledgeTrainer token={token} />
                </div>
            )}
        </div>
    );
}

const fmtTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const today = new Date().toDateString() === d.toDateString();
    return today
        ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
        : d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

export function WhatsAppInbox({ token }) {
    const [convos, setConvos] = useState(null);
    const [active, setActive] = useState(null); // phone
    const [thread, setThread] = useState([]);
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const [aiEnabled, setAiEnabled] = useState(false);
    const [pausedPhones, setPausedPhones] = useState([]);
    const [csvBusy, setCsvBusy] = useState(false);
    const bottomRef = useRef(null);
    const draftRef = useRef(null);

    const downloadCsv = async () => {
        setCsvBusy(true);
        try {
            await exportInboxCsv(token);
            toast.success("Customer CSV downloaded");
        } catch (err) {
            const msg = err.response?.status === 404
                ? "No WhatsApp customer records are available to export."
                : "Export failed — please try again in a moment.";
            toast.error(msg);
        } finally { setCsvBusy(false); }
    };

    const loadConvos = useCallback(async () => {
        try { setConvos((await fetchInboxConversations(token)).conversations); } catch { /* silent poll */ }
    }, [token]);

    const loadAi = useCallback(async () => {
        try {
            const d = await fetchAiSettings(token);
            setAiEnabled(!!d.settings.enabled);
            setPausedPhones(d.paused_phones.map((p) => p.phone));
        } catch { /* silent */ }
    }, [token]);

    const loadThread = useCallback(async (phone) => {
        try { setThread((await fetchInboxMessages(token, phone)).messages); } catch { toast.error("Could not load chat"); }
    }, [token]);

    useEffect(() => { loadConvos(); loadAi(); }, [loadConvos, loadAi]);
    useEffect(() => {
        const t = setInterval(() => { loadConvos(); if (active) loadThread(active); }, 10000);
        return () => clearInterval(t);
    }, [active, loadConvos, loadThread]);
    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread.length]);

    const openChat = async (phone) => {
        setActive(phone);
        await loadThread(phone);
        await markInboxRead(token, phone).catch(() => {});
        loadConvos();
    };

    const send = async () => {
        if (!draft.trim() || !active) return;
        setSending(true);
        try {
            const res = await sendInboxReply(token, { phone: active, text: draft.trim() });
            setThread((t) => [...t, res.message]);
            setDraft("");
            if (draftRef.current) draftRef.current.style.height = "auto";
            loadAi(); // manual reply auto-pauses the AI for this chat
        } catch (err) {
            const d = err.response?.data?.detail;
            toast.error(typeof d === "string" && d.includes("131047")
                ? "24-hour reply window expired — this customer must message you first (Meta policy)"
                : formatApiError(d));
        } finally { setSending(false); }
    };

    const activeConvo = (convos || []).find((c) => c.phone === active);
    const activePaused = active ? pausedPhones.includes(active) : false;

    const toggleAi = async () => {
        try {
            const d = await updateAiSettings(token, { enabled: !aiEnabled });
            setAiEnabled(!!d.enabled);
            toast.success(d.enabled ? "AI auto-reply is ON — it will answer new customer messages" : "AI auto-reply is OFF");
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };

    const togglePause = async () => {
        if (!active) return;
        try {
            if (activePaused) { await resumeAi(token, active); toast.success("AI resumed for this chat"); }
            else { await pauseAi(token, active); toast.success("AI paused for this chat — you're in control"); }
            loadAi();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };

    return (
        <section className="bg-white rounded-2xl border border-stone-300 p-6" data-testid="whatsapp-inbox">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="font-display font-black text-lg text-navy">WhatsApp Inbox</h2>
                    <p className="text-xs text-stone-500 mt-1">Customer chats arriving on your API number. Replies work within 24 hours of the customer's last message (Meta policy).</p>
                </div>
                <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer" data-testid="ai-global-toggle">
                        <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider text-navy"><Bot className="h-3.5 w-3.5" /> AI auto-reply</span>
                        <button onClick={toggleAi} data-testid="ai-global-switch"
                            className={`relative w-11 h-6 rounded-full transition-colors ${aiEnabled ? "bg-[#25D366]" : "bg-stone-300"}`}>
                            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${aiEnabled ? "left-[22px]" : "left-0.5"}`} />
                        </button>
                        <span className={`text-[10px] font-black uppercase ${aiEnabled ? "text-green-600" : "text-stone-400"}`} data-testid="ai-global-state">{aiEnabled ? "On" : "Off"}</span>
                    </label>
                    <button onClick={downloadCsv} disabled={csvBusy} data-testid="inbox-download-csv"
                        className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-navy hover:text-saffron disabled:opacity-50">
                        {csvBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Download Customer CSV
                    </button>
                    <button onClick={() => { loadConvos(); loadAi(); if (active) loadThread(active); }} data-testid="inbox-refresh" className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-navy hover:text-saffron">
                        <RefreshCw className="h-3.5 w-3.5" /> Refresh
                    </button>
                </div>
            </div>

            <AiAgentPanel token={token} pausedPhones={pausedPhones} onChanged={loadAi} />

            {convos === null ? <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-saffron" /></div> : (
                <div className="mt-4 grid md:grid-cols-[280px_1fr] gap-4 min-h-[420px]">
                    {/* conversation list */}
                    <div className="rounded-xl border border-stone-200 overflow-y-auto max-h-[480px]" data-testid="inbox-convo-list">
                        {convos.length === 0 && (
                            <div className="py-10 px-4 text-center">
                                <MessageCircle className="h-6 w-6 text-stone-300 mx-auto" />
                                <p className="mt-2 text-xs text-stone-500">No customer chats yet. Messages sent to your WhatsApp API number will appear here.</p>
                            </div>
                        )}
                        {convos.map((c) => (
                            <button key={c.phone} onClick={() => openChat(c.phone)} data-testid={`inbox-convo-${c.phone}`}
                                className={`w-full text-left px-4 py-3 border-b border-stone-100 hover:bg-cream/60 ${active === c.phone ? "bg-cream" : ""}`}>
                                <div className="flex items-center justify-between gap-2">
                                    <span className="font-bold text-navy text-sm truncate">{c.name || `+${c.phone}`}</span>
                                    <span className="flex items-center gap-1">
                                        {pausedPhones.includes(c.phone) && <span className="rounded-full bg-amber-100 text-amber-700 text-[9px] font-black px-1.5 py-0.5 uppercase" data-testid={`inbox-ai-paused-${c.phone}`}>AI paused</span>}
                                        {c.unread > 0 && <span className="rounded-full bg-[#25D366] text-white text-[10px] font-black px-2 py-0.5" data-testid={`inbox-unread-${c.phone}`}>{c.unread}</span>}
                                    </span>
                                </div>
                                <p className="text-xs text-stone-500 truncate mt-0.5">{c.last?.direction === "outbound" ? "You: " : ""}{c.last?.text}</p>
                                <p className="text-[10px] text-stone-400 mt-0.5">{fmtTime(c.last?.timestamp)}</p>
                            </button>
                        ))}
                    </div>

                    {/* thread */}
                    <div className="rounded-xl border border-stone-200 flex flex-col" data-testid="inbox-thread">
                        {!active ? (
                            <div className="flex-1 flex items-center justify-center text-sm text-stone-400">Select a conversation</div>
                        ) : (
                            <>
                                <div className="px-4 py-3 border-b border-stone-200 flex items-center gap-2">
                                    <span className="font-bold text-navy text-sm" data-testid="inbox-active-name">{activeConvo?.name || `+${active}`}</span>
                                    {activeConvo?.name && <span className="text-xs text-stone-400">+{active}</span>}
                                    <button onClick={togglePause} data-testid="ai-chat-toggle"
                                        className={`ml-auto inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-wider border ${activePaused ? "border-amber-400 text-amber-700 bg-amber-50 hover:bg-amber-100" : "border-stone-300 text-stone-500 hover:border-navy hover:text-navy"}`}>
                                        {activePaused ? <><PlayCircle className="h-3.5 w-3.5" /> Resume AI</> : <><PauseCircle className="h-3.5 w-3.5" /> Pause AI</>}
                                    </button>
                                </div>
                                <div className="flex-1 overflow-y-auto max-h-[340px] px-4 py-3 space-y-2 bg-[#F0EBE3]">
                                    {thread.map((m) => (
                                        <div key={m.id} className={`max-w-[75%] rounded-lg px-3 py-2 text-sm shadow-sm ${m.direction === "outbound" ? "ml-auto bg-[#D9FDD3]" : "bg-white"}`}
                                            data-testid={`inbox-msg-${m.id}`}>
                                            <p className="whitespace-pre-wrap break-words text-stone-800">{m.text}</p>
                                            {m.last_error && (
                                                <p className="mt-1 text-[10px] font-bold text-red-600" title={m.last_error} data-testid={`inbox-error-${m.id}`}>
                                                    ⚠ Not delivered — {m.last_error.slice(0, 60)}{m.last_error.length > 60 ? "…" : ""}
                                                </p>
                                            )}
                                            <p className="text-[9px] text-stone-400 text-right mt-1 flex items-center justify-end gap-1">
                                                {m.ai && <span className="inline-flex items-center gap-0.5 rounded-full bg-navy/10 text-navy px-1.5 py-px font-black uppercase" data-testid={`inbox-ai-badge-${m.id}`}><Bot className="h-2.5 w-2.5" /> AI</span>}
                                                {fmtTime(m.timestamp)}
                                            </p>
                                        </div>
                                    ))}
                                    <div ref={bottomRef} />
                                </div>
                                <div className="p-3 border-t border-stone-200 flex gap-2 items-end">
                                    <textarea ref={draftRef} value={draft} onChange={(e) => setDraft(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                                        onInput={(e) => { e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 140) + "px"; }}
                                        rows={1}
                                        placeholder="Type a reply… (Shift+Enter for a new line)" data-testid="inbox-reply-input"
                                        className="flex-1 rounded-2xl border border-stone-300 bg-white px-4 py-2.5 text-sm outline-none focus:border-saffron resize-none" />
                                    <button onClick={send} disabled={sending || !draft.trim()} data-testid="inbox-send-btn"
                                        className="rounded-full bg-[#25D366] text-white p-2.5 hover:opacity-90 disabled:opacity-50">
                                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}
